import { useEffect, useRef, useState } from 'react';
import { ColorResult } from 'react-color';
import {
  VideoMetadata,
  FontFamily,
  Stroke,
  FontWeight,
  FontStyle,
  CaptionStyle,
} from '@t2c/ai-video-editor';
import {
  CAPTION_FONT_SIZE_MAX,
  CAPTION_FONT_SIZE_MIN,
  CAPTION_Y_POS_MAX,
  CAPTION_Y_POS_MIN,
} from '../../utils';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';

interface Props {
  fontStyle: FontStyle;
  captionStyle: CaptionStyle;
  videoMetadata: VideoMetadata;
  setFontStyle: (value: FontStyle) => void;
  setCaptionStyle: (value: CaptionStyle) => void;
}

export const useEditVideoThemeSetup = ({
  fontStyle,
  captionStyle,
  videoMetadata,
  setFontStyle,
  setCaptionStyle,
}: Props) => {
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [selectedHighlightIndex, setSelectedHighlightIndex] = useState<number | null>(null);
  const [shouldFitText, setShouldFitText] = useState(false);

  const handleFitText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldFitText(event.target.checked);
    setCaptionStyle({ ...captionStyle, enableFitText: event.target.checked });
  };

  const onFontSizeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(
      CAPTION_FONT_SIZE_MAX,
      Math.max(CAPTION_FONT_SIZE_MIN, Number(event.target.value))
    );
    setFontStyle({ ...fontStyle, fontSize: value });
  };

  const onFontSizeSliderChange = (value: number) => {
    setFontStyle({ ...fontStyle, fontSize: value });
  };

  const onYPositionInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const positionYPercent = Math.min(
      CAPTION_Y_POS_MAX,
      Math.max(CAPTION_Y_POS_MIN, Number(event.target.value))
    );
    setCaptionStyle({ ...captionStyle, positionY: positionYPercent });
  };

  const onYPositionSliderChange = (event: Event, newValue: number | number[]) => {
    setCaptionStyle({ ...captionStyle, positionY: newValue as number });
  };

  const handleColorChange = (color: ColorResult) => {
    if (selectedHighlightIndex !== null) {
      setCaptionStyle({
        ...captionStyle,
        [`highlightColor${selectedHighlightIndex + 1}`]: color.hex,
      });
    } else {
      setFontStyle({ ...fontStyle, fontColor: color.hex });
    }
  };

  const handleOnChangeFontFamily = (e: SelectChangeEvent<string>) => {
    setFontStyle({ ...fontStyle, fontFamily: e.target.value as FontFamily });
  };

  const handleOnChangeFontWeight = (e: SelectChangeEvent<number>) => {
    setFontStyle({ ...fontStyle, fontWeight: e.target.value as FontWeight });
  };

  useEffect(() => {
    if (captionStyle?.enableFitText !== shouldFitText) {
      setShouldFitText(!!captionStyle?.enableFitText);
    }
  }, [captionStyle?.enableFitText]);

  // useEffect(() => {
  //   if (videoMetadata?.height) {
  //     captionPosY.current = Math.round((captionYPositionPercent / 100) * videoMetadata.height);
  //   }
  // }, [videoMetadata]);

  return {
    selectedHighlightIndex,
    isColorPickerOpen,
    setSelectedHighlightIndex,
    setIsColorPickerOpen,
    handleOnChangeFontFamily,
    handleOnChangeFontWeight,
    onFontSizeInputChange,
    onFontSizeSliderChange,
    onYPositionInputChange,
    onYPositionSliderChange,
    handleColorChange,
    shouldFitText,
    handleFitText,
  };
};
